import EzController from 'controllers/ez-base-controller';
import { padZero } from 'lib/helpers';
import MediaMatcher from 'lib/media-matcher';

const mediaMatcher = new MediaMatcher();

export default class CountdownTimerController extends EzController {
  declare readonly countdownEndValue:string;

  declare readonly mobileDisableWidthValue:number;

  declare readonly daysTextValue:string;

  declare readonly hoursTextValue:string;

  declare readonly minutesTextValue:string;

  declare readonly secondsTextValue:string;

  declare readonly inlineValue:boolean;

  declare readonly hasMobileDisableWidthValue:boolean;

  declare countdownDate:number;

  declare days:number;

  declare hours:number;

  declare minutes:number;

  declare seconds:number;

  static values = {
    mobileDisableWidth: Number,
    countdownEnd: String,
    daysText: String,
    hoursText: String,
    minutesText: String,
    secondsText: String,
    inline: Boolean,
  }

  timer = null;

  connect() {
    const normalizedDateString = this.countdownEndValue.replace(/"/g, '');
    this.countdownDate = new Date(normalizedDateString).getTime();

    if (this.hasMobileDisableWidthValue) {
      mediaMatcher.addMediaQuery(`(min-width: ${this.mobileDisableWidthValue}px)`,
        () => { this.startTimer(); },
        () => { if (this.timer) clearInterval(this.timer); });

      mediaMatcher.evaluateAll();
      mediaMatcher.addListeners();
    } else {
      this.startTimer();
    }
  }

  disconnect() {
    if (this.timer) clearInterval(this.timer);

    if (this.hasMobileDisableWidthValue) {
      mediaMatcher.removeListeners();
      mediaMatcher.clearQueries();
    }
  }

  startTimer() {
    this.timer = setInterval(this.updateTimer, 1000);
  }

  updateTimer = () => {
    const now = new Date().getTime();
    const distance = this.countdownDate - now;

    this.days = this.calculateTimerValue({ part: 'days', distance });
    this.hours = this.calculateTimerValue({ part: 'hours', distance });
    this.minutes = this.calculateTimerValue({ part: 'minutes', distance });
    this.seconds = this.calculateTimerValue({ part: 'seconds', distance });

    if (this.inlineValue) {
      this.renderInlineOutput();
    } else {
      this.renderBlockOutput();
    }

    this.flagLoaded();

    if (distance < 0) clearInterval(this.timer);
  }

  calculateTimerValue({ part, distance }:{ part:string, distance:number }) {
    switch (part) {
      case 'days':
        return Math.floor(distance / (1000 * 60 * 60 * 24));
      case 'hours':
        return Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      case 'minutes':
        return Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      case 'seconds':
      default:
        return Math.floor((distance % (1000 * 60)) / 1000);
    }
  }

  renderInlineOutput() {
    const output:string[] = [
      padZero(this.minutes),
      padZero(this.seconds),
    ];

    if (this.days) {
      output.unshift(padZero(this.hours));
      output.unshift(`${this.days}`);
    } else {
      output.unshift(`${this.hours}`);
    }

    this.element.innerHTML = output.join(':');
  }

  renderBlockOutput() {
    const output:string[] = [
      this.renderTimerBlock('hours'),
      this.renderTimerBlock('minutes'),
      this.renderTimerBlock('seconds'),
    ];

    if (this.days) {
      output.unshift(this.renderTimerBlock('days'));
    }

    this.element.innerHTML = output.join('');
  }

  renderTimerBlock(timerUnit:string) {
    return `<span class="countdown-timer__unit-block">
              <span class="countdown-timer__unit-block__unit-value">${this[timerUnit]}</span>
              <span class="countdown-timer__unit-block__unit-text">${this[`${timerUnit}TextValue`]}</span>
            </span>`;
  }
}
